import React, {useState, useEffect, useCallback} from 'react';
import {connect, useSelector} from 'react-redux';
import { makeStyles } from '@mui/styles';
import connectedIcon from '../../../assets/images/gateway/connected.png';
import disconnectedIcon from '../../../assets/images/gateway/disconnected.png';
import plansIcon from '../../../assets/images/plans.png';
import errorIcon from '../../../assets/images/error.png';
import warningIcon from '../../../assets/images/warning.png';
import Api from '../../../api/SMPFetchHelper';
import RowLayout from '../../Layouts/RowLayout';
import {useTranslation} from 'react-i18next';
import {isEmptyPortalSelector, portalTimeZoneSelector} from '../../../store/selectors/user.selectors';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import dayjs from 'dayjs';
import {SMCPaths} from '../../../conf/Routes';
import {push} from 'connected-react-router';
import {ObjectTypes} from '../../../api/consts';
import {dayjsWithPortalTimezone} from '../../../utils/utils';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {setPortalTimeZone} from '../../../store/actions/user.actions';

const Sepperator = () => <span style={{ color: 'white', marginLeft: '10px', marginRight: '10px' }}>|</span>;

function SMPMainFooter({
    isEmptyPortal,
    navigateTo,
    setPortalTimeZone
}) {
    const { t } = useTranslation();
    const classes = useStyles();
    const { getErrorMessage } = useSMCErrorHandler();
    const portalTimeZone = useSelector(portalTimeZoneSelector);

    const [isLoading, setIsLoading] = useState(true);
    const [/*errorMessage*/, setErrorMessage] = useState(null);

    const [connectedGatewaysCount, setConnectedGatewaysCount] = useState(null);
    const [disconnectedGatewaysCount, setDisconnectedGatewaysCount] = useState(null);
    const [plansCount, setPlansCount] = useState(null);
    const [failedTasksCount, setFailedTasksCount] = useState(null);
    const [errorMessages, setErrorMessages] = useState([]);
    const [updateTimestamp, setUpdateTimestamp] = useState(null);

    const loadData = useCallback(() => {
        return new Promise(async (resolve, reject) => {
            try {
                let connected;
                let disconnected;
                let disabled;
                let plansCount;
                let errorMessages;
                let failedTasksCount;
                let timestamp;

                if (isEmptyPortal) {
                    const [getSessionDataResp] = await Promise.all([
                        Api.getSessionData({
                            attributes: ['failedTasksCount']
                        })
                    ]);
                    failedTasksCount = getSessionDataResp.data[SessionDataGetSchema.failedTasksCount];
                }
                if (!isEmptyPortal) {
                    const [gatewaysStatusResp, getPlanCountResp, getSessionDataResp] = await Promise.all([
                        Api.getGatewaysStatus(),
                        Api.getObjectsCount(ObjectTypes.PLAN),
                        Api.getSessionData({
                            attributes: [
                                SessionDataGetSchema.failedTasksCount,
                                SessionDataGetSchema.serviceCenterTimeZone
                            ]
                        })
                    ]);
                    connected = gatewaysStatusResp.data.connected;
                    disconnected = gatewaysStatusResp.data.disconnected;
                    disabled = gatewaysStatusResp.data.disabled;
                    plansCount = getPlanCountResp.data.data.count;
                    failedTasksCount = getSessionDataResp.data[SessionDataGetSchema.failedTasksCount];
                    const newServiceCenterTimeZone = getSessionDataResp.data[SessionDataGetSchema.serviceCenterTimeZone];
                    if (newServiceCenterTimeZone !== portalTimeZone) {
                        setPortalTimeZone(newServiceCenterTimeZone);
                    }
                }
                try {
                    timestamp = dayjsWithPortalTimezone(dayjs());
                } catch (e) {
                    timestamp = dayjs();
                }
                return resolve({ connected, disconnected, disabled, plansCount, errorMessages, failedTasksCount, timestamp });
            } catch (e) {
                return reject(e);
            }
        })
    }, [isEmptyPortal, portalTimeZone, setPortalTimeZone]);

    useEffect(() => {
        let isSubscribed = true;

        const loadPageData = () => {
            setIsLoading(true);
            loadData().then(data => {
                if (isSubscribed) {
                    setConnectedGatewaysCount(data.connected);
                    setDisconnectedGatewaysCount(data.disconnected);
                    setPlansCount(data.plansCount);
                    setFailedTasksCount(data.failedTasksCount);
                    setIsLoading(false);
                    setErrorMessages(data.errorMessages);
                    setUpdateTimestamp(data.timestamp);
                    // Set this function to run again in 30 seconds
                    // Note this will only be called if still subscribed and not error
                    setTimeout(loadPageData, 30 * 1000);
                }
            }).catch(e => {
                const errorMessage = getErrorMessage(e);
                setErrorMessage(errorMessage);
                setIsLoading(false);
            });
        }
        loadPageData();

        return () => {
            isSubscribed = false;
        }
    }, [isEmptyPortal, getErrorMessage, loadData, t]);

    return (
        <div className={classes.root}>
            {
                // Lodaing mask
                isLoading && (
                    <div className={classes.loadingMaskRoot}>
                        <div className={classes.loadingContainer}>
                            <div className={classes.loadingBox}>
                                Loading...
                            </div>
                        </div>
                    </div>
                )
            }

            {/* left */}
            <div className={classes.leftSectionRoot}>
                <RowLayout>
                    {
                        !isEmptyPortal && (
                            <>
                            <span>
                                <span className={classes.linkSpan} onClick={() => navigateTo(SMCPaths.gateways)}>
                                    <b>{t('SMC_STATUS_GWUSAGE')}&nbsp;&nbsp;</b>
                                </span>
                                <img className={classes.img} src={connectedIcon} alt=""/>
                                <span className={classes.linkSpan}
                                      onClick={() => navigateTo(SMCPaths.gateways + '?status=connected')}>
                                    <b>{connectedGatewaysCount}</b> {t('SMC_GATEWAY_STATUS_CONNECTED')}&nbsp;&nbsp;&nbsp;
                                </span>
                            </span>
                                <span>
                                <img className={classes.img} src={disconnectedIcon} alt=""/>
                                <span className={classes.linkSpan}
                                      onClick={() => navigateTo(SMCPaths.gateways + '?status=disconnected')}>
                                    <b>{disconnectedGatewaysCount}</b> {t('SMC_GATEWAY_STATUS_DISCONNECTED')}
                                </span>
                            </span>
                                <Sepperator/>
                                <span>
                                <img className={classes.img} src={plansIcon} alt=""/>
                                <span className={classes.linkSpan} onClick={() => navigateTo(SMCPaths.plans)}>
                                    <b>{plansCount}</b> {t('SMC_WELCOME_PLANS')}
                                </span>
                            </span>
                            </>
                        )
                    }
                    {
                        failedTasksCount > 0 && (
                            <span>
                                <Sepperator/>
                                <img className={classes.img} src={errorIcon} alt=""/>
                                <b>{failedTasksCount}</b> Failed Tasks
                            </span>
                        )
                    }
                    {
                        errorMessages && errorMessages.map((msg, index) =>
                            <span key={index}>
                                <Sepperator/>
                                <img className={classes.img} src={warningIcon} alt="warning"/>
                                {msg}
                            </span>
                        )
                    }
                </RowLayout>
            </div>
            {/* right */}
            <span className={classes.date}>
                {updateTimestamp && dayjs(updateTimestamp).format('DD MMMM HH:mm:ss [GMT] Z')}
            </span>
        </div>
    );
}

const useStyles = makeStyles({
    root: {
        height: '100%',
        width: '100%',
        background: '#171F2C',
        color: '#E8F1FF',
        fontSize: '13px',
        lineHeight: '23px',
        paddingTop: '1px',
        boxSizing: 'border-box',
        overflow: 'hidden'
    },
    img: {
        position: 'relative',
        top: '3px',
        paddingRight: '3px'
    },
    date: {
        fontWeight: 'bold',
        fontSize: '12px',
        color: '#d2d3d6',
        float: 'right',
        margin: '0px 10px',
    },
    linkSpan: {
        cursor: 'pointer'
    },
    loadingMaskRoot: {
        width: '100%',
        position: 'absolute',
        textAlign: 'center',
        background: '#f7f7f780',
        zIndex: 999
    },
    loadingContainer: {
        textAlign: 'center',
        display: 'inline-block'
    },
    loadingBox: {
        width: '130px',
        backgroundColor: '#e0e4e8',
        color: '#333'
    },
    leftSectionRoot: {
        float: 'left',
        paddingLeft: '10px',
        whiteSpace: 'nowrap'
    }
})

const mapStateToProps = state => ({
    isEmptyPortal: isEmptyPortalSelector(state)
});

const mapDispatchToProps = {
    navigateTo: push,
    setPortalTimeZone
}

export default connect(mapStateToProps, mapDispatchToProps)(SMPMainFooter);